import type { BundleImportPreview, BundleInput } from '@flux/shared';
import { useMutation } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { Field } from '@/components/ui/field.js';
import { Input } from '@/components/ui/input.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { toast } from '@/lib/toast.js';
import { confirmBundleImport, previewBundleImport, readFileAsText } from './api.js';

export interface BundleImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: () => void;
}

/**
 * « Importer un CSV » (`02` §8) : format matriciel v1 (`Shopify-replace-sku*`)
 * ou format simple (`bundle_sku;component_sku;qty;price`), détectés
 * automatiquement côté serveur (`format`/`matrixMode` omis). Aperçu des
 * bundles créés/modifiés avant confirmation ; la confirmation renvoie
 * exactement `[...preview.created, ...preview.updated]`, sans reparser le CSV
 * (contrat de `BundleImportConfirmBody`).
 */
export function BundleImportDialog({
  open,
  onOpenChange,
  onImported,
}: BundleImportDialogProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BundleImportPreview | null>(null);

  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Sélectionnez un fichier.');
      }
      const content = await readFileAsText(file);
      return previewBundleImport({ content });
    },
    onSuccess: (data) => {
      setPreview(data);
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      if (!preview) {
        throw new Error('Aucun aperçu à confirmer.');
      }
      return confirmBundleImport([...preview.created, ...preview.updated]);
    },
    onSuccess: (result) => {
      toast.success(
        `${String(result.created)} bundle(s) créé(s), ${String(result.updated)} modifié(s).`,
      );
      onImported();
      reset();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });

  function reset(): void {
    setFile(null);
    setPreview(null);
    previewMutation.reset();
    confirmMutation.reset();
  }

  const totalChanges = (preview?.created.length ?? 0) + (preview?.updated.length ?? 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Importer un CSV</DialogTitle>
          <DialogDescription>
            Une ligne par composant : le SKU du bundle, le SKU du composant, la quantité et un prix
            unitaire facultatif. Rien n’est écrit avant que vous ayez vu l’aperçu.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5">
          <Field
            label="Fichier CSV"
            htmlFor="bundle-import-file"
            hint={
              <>
                Les anciens tableaux Google Sheets (« Shopify-replace-sku… ») sont reconnus tels
                quels.{' '}
                <a
                  href="/exemples/bundles-exemple.csv"
                  download
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Télécharger un exemple
                </a>
              </>
            }
          >
            <Input
              id="bundle-import-file"
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
                setPreview(null);
                previewMutation.reset();
              }}
            />
          </Field>

          {preview ? (
            <div className="flex flex-col gap-4 border-t border-border pt-4">
              <p className="text-sm text-muted-foreground">
                Format détecté : {preview.format === 'matrix' ? 'matriciel' : 'simple'}
                {preview.matrixMode
                  ? ` (${preview.matrixMode === 'components' ? 'composants' : 'lookup'})`
                  : ''}{' '}
                — <span className="tabular-nums">{preview.unchanged} bundle(s) inchangé(s).</span>
              </p>

              {preview.errors.length > 0 ? (
                <ul className="list-disc pl-4 text-sm text-status-red-fg">
                  {preview.errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              ) : null}
              {preview.warnings.length > 0 ? (
                <ul className="list-disc pl-4 text-sm text-status-orange-fg">
                  {preview.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              ) : null}

              {preview.created.length > 0 ? (
                <ImportPreviewTable
                  title={`À créer (${String(preview.created.length)})`}
                  items={preview.created}
                />
              ) : null}

              {preview.updated.length > 0 ? (
                <ImportPreviewTable
                  title={`À modifier (${String(preview.updated.length)})`}
                  items={preview.updated}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Fermer
          </Button>
          {!preview ? (
            <Button
              onClick={() => {
                previewMutation.mutate();
              }}
              disabled={!file}
              isLoading={previewMutation.isPending}
            >
              Analyser
            </Button>
          ) : (
            <Button
              onClick={() => {
                confirmMutation.mutate();
              }}
              disabled={totalChanges === 0}
              isLoading={confirmMutation.isPending}
            >
              Confirmer l’import
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Bundles à créer ou à modifier dans l'aperçu : SKU, libellé, nombre de composants. */
function ImportPreviewTable({
  title,
  items,
}: {
  title: string;
  items: BundleInput[];
}): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold tabular-nums">{title}</h3>
      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>SKU</TableHead>
              <TableHead>Libellé</TableHead>
              <TableHead className="text-right">Composants</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.sku}>
                <TableCell className="whitespace-nowrap">{item.sku}</TableCell>
                <TableCell>
                  {item.label && item.label !== item.sku ? (
                    item.label
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{item.components.length}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
